import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, Globe } from 'lucide-react';
import { ContentSection, TextField, TextArea } from './FormFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CORE_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' }
];

const ContentTab = ({
  content,
  activeLang,
  setActiveLang,
  expandedSections,
  toggleSection,
  getFieldValue,
  setFieldValue,
  getNestedFieldValue,
  setNestedFieldValue,
  getArrayItemValue,
  setArrayItemValue,
  addArrayItem,
  removeArrayItem
}) => {
  const [languages, setLanguages] = useState(CORE_LANGUAGES);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/languages`)
      .then(res => {
        const langs = (res.data.languages || []).map(l => ({
          code: l.code,
          label: l.nativeName || l.name,
          flag: l.flag
        }));
        if (langs.length) setLanguages(langs);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      {/* Language Selector */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Globe className="w-4 h-4" />
            <span className="font-medium">Editing Language:</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => setActiveLang(lang.code)}
                data-testid={`content-lang-${lang.code}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeLang === lang.code 
                    ? 'bg-slate-900 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {lang.flag} {lang.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <ContentSection title="Hero Section" expanded={expandedSections.hero} onToggle={() => toggleSection('hero')}>
        <TextField label="Headline" value={getFieldValue(content.hero, 'headline')} onChange={(v) => setFieldValue('hero', 'headline', v)} />
        <TextField label="Subheadline" value={getFieldValue(content.hero, 'subheadline')} onChange={(v) => setFieldValue('hero', 'subheadline', v)} />
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Primary Button" value={getFieldValue(content.hero, 'ctaPrimary')} onChange={(v) => setFieldValue('hero', 'ctaPrimary', v)} />
          <TextField label="Secondary Button" value={getFieldValue(content.hero, 'ctaSecondary')} onChange={(v) => setFieldValue('hero', 'ctaSecondary', v)} />
        </div>
      </ContentSection>

      {/* Teasers */}
      <ContentSection title="About Teaser" expanded={expandedSections.aboutTeaser} onToggle={() => toggleSection('aboutTeaser')}>
        <TextField label="Label" value={getFieldValue(content.aboutTeaser, 'label')} onChange={(v) => setFieldValue('aboutTeaser', 'label', v)} />
        <TextArea label="Text" value={getFieldValue(content.aboutTeaser, 'text')} onChange={(v) => setFieldValue('aboutTeaser', 'text', v)} rows={2} />
        <TextField label="Link Text" value={getFieldValue(content.aboutTeaser, 'linkText')} onChange={(v) => setFieldValue('aboutTeaser', 'linkText', v)} />
      </ContentSection>

      <ContentSection title="Mission Teaser" expanded={expandedSections.missionTeaser} onToggle={() => toggleSection('missionTeaser')}>
        <TextField label="Label" value={getFieldValue(content.missionTeaser, 'label')} onChange={(v) => setFieldValue('missionTeaser', 'label', v)} />
        <TextArea label="Text" value={getFieldValue(content.missionTeaser, 'text')} onChange={(v) => setFieldValue('missionTeaser', 'text', v)} rows={2} />
        <TextField label="Link Text" value={getFieldValue(content.missionTeaser, 'linkText')} onChange={(v) => setFieldValue('missionTeaser', 'linkText', v)} />
      </ContentSection>

      <ContentSection title="Story Teaser" expanded={expandedSections.storyTeaser} onToggle={() => toggleSection('storyTeaser')}>
        <TextField label="Label" value={getFieldValue(content.storyTeaser, 'label')} onChange={(v) => setFieldValue('storyTeaser', 'label', v)} />
        <TextArea label="Text" value={getFieldValue(content.storyTeaser, 'text')} onChange={(v) => setFieldValue('storyTeaser', 'text', v)} rows={2} />
        <TextField label="Link Text" value={getFieldValue(content.storyTeaser, 'linkText')} onChange={(v) => setFieldValue('storyTeaser', 'linkText', v)} />
      </ContentSection>

      {/* VMV */}
      <ContentSection title="Vision, Mission & Values" expanded={expandedSections.vmv} onToggle={() => toggleSection('vmv')}>
        <TextField label="Section Label" value={getFieldValue(content.visionMissionValues, 'sectionLabel')} onChange={(v) => setFieldValue('visionMissionValues', 'sectionLabel', v)} />
        <TextField label="Section Title" value={getFieldValue(content.visionMissionValues, 'sectionTitle')} onChange={(v) => setFieldValue('visionMissionValues', 'sectionTitle', v)} />
        <div className="border-t pt-4 mt-4">
          <h4 className="font-medium text-slate-700 mb-3">Vision</h4>
          <TextField label="Label" value={getNestedFieldValue('visionMissionValues', 'vision', 'label')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'vision', 'label', v)} />
          <TextArea label="Text" value={getNestedFieldValue('visionMissionValues', 'vision', 'text')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'vision', 'text', v)} rows={3} />
        </div>
        <div className="border-t pt-4 mt-4">
          <h4 className="font-medium text-slate-700 mb-3">Mission</h4>
          <TextField label="Label" value={getNestedFieldValue('visionMissionValues', 'mission', 'label')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'mission', 'label', v)} />
          <TextArea label="Text" value={getNestedFieldValue('visionMissionValues', 'mission', 'text')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'mission', 'text', v)} rows={3} />
        </div>
        <div className="border-t pt-4 mt-4">
          <h4 className="font-medium text-slate-700 mb-3">Values</h4>
          <TextField label="Label" value={getNestedFieldValue('visionMissionValues', 'values', 'label')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'values', 'label', v)} />
          <TextArea label="Text" value={getNestedFieldValue('visionMissionValues', 'values', 'text')} onChange={(v) => setNestedFieldValue('visionMissionValues', 'values', 'text', v)} rows={3} />
        </div>
        <TextField label="Read More Text" value={getFieldValue(content.visionMissionValues, 'readMoreText')} onChange={(v) => setFieldValue('visionMissionValues', 'readMoreText', v)} />
      </ContentSection>

      {/* What We Offer */}
      <ContentSection title="What We Offer" expanded={expandedSections.whatWeOffer} onToggle={() => toggleSection('whatWeOffer')}>
        <TextField label="Section Label" value={getFieldValue(content.whatWeOffer, 'label')} onChange={(v) => setFieldValue('whatWeOffer', 'label', v)} />
        <TextField label="Section Title" value={getFieldValue(content.whatWeOffer, 'title')} onChange={(v) => setFieldValue('whatWeOffer', 'title', v)} />
        <div className="space-y-3 mt-4">
          {(content.whatWeOffer?.items || []).map((item, index) => (
            <div key={`offer-${index}`} className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3 relative">
              <button onClick={() => removeArrayItem('whatWeOffer', 'items', index)} className="absolute top-1 right-1 p-1 text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
              <TextField label="Title" value={getArrayItemValue('whatWeOffer', 'items', index, 'title')} onChange={(v) => setArrayItemValue('whatWeOffer', 'items', index, 'title', v)} />
              <TextField label="Description" value={getArrayItemValue('whatWeOffer', 'items', index, 'description')} onChange={(v) => setArrayItemValue('whatWeOffer', 'items', index, 'description', v)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('whatWeOffer', 'items', { title: { en: '', ko: '', ja: '' }, description: { en: '', ko: '', ja: '' } })} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><Plus className="w-4 h-4" /> Add Item</button>
        </div>
      </ContentSection>

      {/* Why Choose Us */}
      <ContentSection title="Why Choose Us" expanded={expandedSections.whyChooseUs} onToggle={() => toggleSection('whyChooseUs')}>
        <TextField label="Section Label" value={getFieldValue(content.whyChooseUs, 'label')} onChange={(v) => setFieldValue('whyChooseUs', 'label', v)} />
        <TextField label="Section Title" value={getFieldValue(content.whyChooseUs, 'title')} onChange={(v) => setFieldValue('whyChooseUs', 'title', v)} />
        <div className="space-y-3 mt-4">
          {(content.whyChooseUs?.items || []).map((item, index) => (
            <div key={`why-${index}`} className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3 relative">
              <button onClick={() => removeArrayItem('whyChooseUs', 'items', index)} className="absolute top-1 right-1 p-1 text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
              <TextField label="Title" value={getArrayItemValue('whyChooseUs', 'items', index, 'title')} onChange={(v) => setArrayItemValue('whyChooseUs', 'items', index, 'title', v)} />
              <TextField label="Description" value={getArrayItemValue('whyChooseUs', 'items', index, 'description')} onChange={(v) => setArrayItemValue('whyChooseUs', 'items', index, 'description', v)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('whyChooseUs', 'items', { title: { en: '', ko: '', ja: '' }, description: { en: '', ko: '', ja: '' } })} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><Plus className="w-4 h-4" /> Add Item</button>
        </div>
      </ContentSection>

      {/* Testimonials */}
      <ContentSection title="Testimonials" expanded={expandedSections.testimonials} onToggle={() => toggleSection('testimonials')}>
        <TextField label="Section Label" value={getFieldValue(content.testimonials, 'label')} onChange={(v) => setFieldValue('testimonials', 'label', v)} />
        <TextField label="Section Title" value={getFieldValue(content.testimonials, 'title')} onChange={(v) => setFieldValue('testimonials', 'title', v)} />
        <div className="space-y-4 mt-4">
          {(content.testimonials?.items || []).map((item, index) => (
            <div key={`test-${index}`} className="bg-slate-50 rounded-lg p-4 relative">
              <button onClick={() => removeArrayItem('testimonials', 'items', index)} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              <TextArea label={`Testimonial ${index + 1}`} value={getArrayItemValue('testimonials', 'items', index, 'text')} onChange={(v) => setArrayItemValue('testimonials', 'items', index, 'text', v)} rows={2} />
              <TextField label="Author" value={getArrayItemValue('testimonials', 'items', index, 'author')} onChange={(v) => setArrayItemValue('testimonials', 'items', index, 'author', v)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('testimonials', 'items', { text: { en: '', ko: '', ja: '' }, author: { en: '', ko: '', ja: '' } })} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><Plus className="w-4 h-4" /> Add Testimonial</button>
        </div>
      </ContentSection>

      {/* How It Works */}
      <ContentSection title="How It Works" expanded={expandedSections.howItWorks} onToggle={() => toggleSection('howItWorks')}>
        <TextField label="Section Label" value={getFieldValue(content.howItWorks, 'label')} onChange={(v) => setFieldValue('howItWorks', 'label', v)} />
        <TextField label="Section Title" value={getFieldValue(content.howItWorks, 'title')} onChange={(v) => setFieldValue('howItWorks', 'title', v)} />
        <div className="space-y-3 mt-4">
          {(content.howItWorks?.steps || []).map((step, index) => (
            <div key={`step-${index}`} className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3">
              <TextField label={`Step ${index + 1} Title`} value={getArrayItemValue('howItWorks', 'steps', index, 'title')} onChange={(v) => setArrayItemValue('howItWorks', 'steps', index, 'title', v)} />
              <TextField label="Description" value={getArrayItemValue('howItWorks', 'steps', index, 'description')} onChange={(v) => setArrayItemValue('howItWorks', 'steps', index, 'description', v)} />
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <ContentSection title="FAQ" expanded={expandedSections.faq} onToggle={() => toggleSection('faq')}>
        <TextField label="Section Label" value={getFieldValue(content.faq, 'label')} onChange={(v) => setFieldValue('faq', 'label', v)} />
        <TextField label="Section Title" value={getFieldValue(content.faq, 'title')} onChange={(v) => setFieldValue('faq', 'title', v)} />
        <div className="space-y-4 mt-4">
          {(content.faq?.items || []).map((item, index) => (
            <div key={`faq-${index}`} className="bg-slate-50 rounded-lg p-4 relative">
              <button onClick={() => removeArrayItem('faq', 'items', index)} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              <TextField label={`Question ${index + 1}`} value={getArrayItemValue('faq', 'items', index, 'question')} onChange={(v) => setArrayItemValue('faq', 'items', index, 'question', v)} />
              <TextArea label="Answer" value={getArrayItemValue('faq', 'items', index, 'answer')} onChange={(v) => setArrayItemValue('faq', 'items', index, 'answer', v)} rows={3} />
            </div>
          ))}
          <button onClick={() => addArrayItem('faq', 'items', { question: { en: '', ko: '', ja: '' }, answer: { en: '', ko: '', ja: '' } })} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><Plus className="w-4 h-4" /> Add FAQ</button>
        </div>
      </ContentSection>

      {/* Final CTA */}
      <ContentSection title="Final CTA" expanded={expandedSections.finalCta} onToggle={() => toggleSection('finalCta')}>
        <TextField label="Title" value={getFieldValue(content.finalCta, 'title')} onChange={(v) => setFieldValue('finalCta', 'title', v)} />
        <TextField label="Subtitle" value={getFieldValue(content.finalCta, 'subtitle')} onChange={(v) => setFieldValue('finalCta', 'subtitle', v)} />
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Primary Button" value={getFieldValue(content.finalCta, 'ctaPrimary')} onChange={(v) => setFieldValue('finalCta', 'ctaPrimary', v)} />
          <TextField label="Secondary Button" value={getFieldValue(content.finalCta, 'ctaSecondary')} onChange={(v) => setFieldValue('finalCta', 'ctaSecondary', v)} />
        </div>
      </ContentSection>

      {/* Footer */}
      <ContentSection title="Footer" expanded={expandedSections.footer} onToggle={() => toggleSection('footer')}>
        <TextField label="Mission Statement" value={getFieldValue(content.footer, 'missionStatement')} onChange={(v) => setFieldValue('footer', 'missionStatement', v)} />
        <TextField label="Copyright" value={getFieldValue(content.footer, 'copyright')} onChange={(v) => setFieldValue('footer', 'copyright', v)} />
      </ContentSection>

      {/* About Page */}
      <ContentSection title="About Page" expanded={expandedSections.aboutPage} onToggle={() => toggleSection('aboutPage')}>
        <TextField label="Header Label" value={getFieldValue(content.aboutPage, 'headerLabel')} onChange={(v) => setFieldValue('aboutPage', 'headerLabel', v)} />
        <TextField label="Header Title" value={getFieldValue(content.aboutPage, 'headerTitle')} onChange={(v) => setFieldValue('aboutPage', 'headerTitle', v)} />
        <TextArea label="Header Subtitle" value={getFieldValue(content.aboutPage, 'headerSubtitle')} onChange={(v) => setFieldValue('aboutPage', 'headerSubtitle', v)} rows={2} />
        <TextField label="Vision Title" value={getFieldValue(content.aboutPage, 'visionTitle')} onChange={(v) => setFieldValue('aboutPage', 'visionTitle', v)} />
        <TextArea label="Vision Text" value={getFieldValue(content.aboutPage, 'visionText')} onChange={(v) => setFieldValue('aboutPage', 'visionText', v)} rows={4} />
        <TextField label="Mission Title" value={getFieldValue(content.aboutPage, 'missionTitle')} onChange={(v) => setFieldValue('aboutPage', 'missionTitle', v)} />
        <TextArea label="Mission Text" value={getFieldValue(content.aboutPage, 'missionText')} onChange={(v) => setFieldValue('aboutPage', 'missionText', v)} rows={4} />
        <TextField label="Story Title" value={getFieldValue(content.aboutPage, 'storyTitle')} onChange={(v) => setFieldValue('aboutPage', 'storyTitle', v)} />
        <TextArea label="Story Text 1" value={getFieldValue(content.aboutPage, 'storyText1')} onChange={(v) => setFieldValue('aboutPage', 'storyText1', v)} rows={3} />
        <TextArea label="Story Text 2" value={getFieldValue(content.aboutPage, 'storyText2')} onChange={(v) => setFieldValue('aboutPage', 'storyText2', v)} rows={3} />
        <TextArea label="Story Text 3" value={getFieldValue(content.aboutPage, 'storyText3')} onChange={(v) => setFieldValue('aboutPage', 'storyText3', v)} rows={3} />
        <TextField label="CTA Title" value={getFieldValue(content.aboutPage, 'ctaTitle')} onChange={(v) => setFieldValue('aboutPage', 'ctaTitle', v)} />
        <TextField label="CTA Button" value={getFieldValue(content.aboutPage, 'ctaButton')} onChange={(v) => setFieldValue('aboutPage', 'ctaButton', v)} />
      </ContentSection>

      {/* Contact Page */}
      <ContentSection title="Contact Page" expanded={expandedSections.contactPage} onToggle={() => toggleSection('contactPage')}>
        <TextField label="Header Label" value={getFieldValue(content.contactPage, 'headerLabel')} onChange={(v) => setFieldValue('contactPage', 'headerLabel', v)} />
        <TextField label="Header Title" value={getFieldValue(content.contactPage, 'headerTitle')} onChange={(v) => setFieldValue('contactPage', 'headerTitle', v)} />
        <TextField label="Header Subtitle" value={getFieldValue(content.contactPage, 'headerSubtitle')} onChange={(v) => setFieldValue('contactPage', 'headerSubtitle', v)} />
        <TextField label="Form Title" value={getFieldValue(content.contactPage, 'formTitle')} onChange={(v) => setFieldValue('contactPage', 'formTitle', v)} />
        <TextField label="Submit Button" value={getFieldValue(content.contactPage, 'formSubmitButton')} onChange={(v) => setFieldValue('contactPage', 'formSubmitButton', v)} />
        <TextField label="Success Title" value={getFieldValue(content.contactPage, 'successTitle')} onChange={(v) => setFieldValue('contactPage', 'successTitle', v)} />
        <TextField label="Success Text" value={getFieldValue(content.contactPage, 'successText')} onChange={(v) => setFieldValue('contactPage', 'successText', v)} />
      </ContentSection>
    </div>
  );
};

export default ContentTab;
